import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import { ArrowLeft, ArrowRight, CancelTwoTone } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { IconButton, Modal, Tooltip, Typography } from "@mui/material";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

import styles from "./ExpandedMediaDisplay.module.scss";
import LazyLoadVideo from "./LazyLoadVideo";

/**
 * The area the expanded viewer fills, which is the work area rather than the window: covering the
 * navigation loses the reader their place for the sake of a few pixels of photo.
 *
 * `.alert-pane` is looked for first, because side by side there are two of them in one drawer and
 * a photo belongs to the bulletin it was opened from: scoping to the drawer covered both, so the
 * other bulletin could not be scrolled or have its own photo opened for the comparison the split
 * view exists for. The pane is the one element per bulletin that is positioned and does not
 * scroll -- `.alert-container` inside it is the scroller, and an `inset: 0` overlay in a scrolled
 * container lands above the visible area, which is why it stays the last resort for the callers
 * that have no pane.
 */
const findScope = (anchor: HTMLElement | null) =>
  anchor?.closest<HTMLElement>(".alert-pane") ??
  anchor?.closest<HTMLElement>(".MuiDrawer-paper") ??
  anchor?.closest<HTMLElement>(".alert-container") ??
  null;

interface CompareMediaDisplayProps {
  isSlideShowActive: boolean;
  setIsSlideShowActive: React.Dispatch<React.SetStateAction<boolean>>;
  mediaSrc: string;
  mediaType: "image" | "video";
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
  slideshowLength: number;
}

const CompareMediaDisplay = ({
  isSlideShowActive,
  setIsSlideShowActive,
  mediaSrc,
  mediaType,
  selectedIndex,
  setSelectedIndex,
  slideshowLength,
}: CompareMediaDisplayProps) => {
  const portalAnchorRef = useRef<HTMLSpanElement>(null);
  const [isScoped, setIsScoped] = useState(false);

  useLayoutEffect(() => {
    setIsScoped(findScope(portalAnchorRef.current) !== null);
  }, []);

  useEffect(() => {
    if (selectedIndex === -1) {
      setIsSlideShowActive(false);
      return;
    }
    setIsSlideShowActive(true);
  }, [selectedIndex]);

  useLayoutEffect(() => {
    if (!isSlideShowActive) return;

    const scope = findScope(portalAnchorRef.current);
    if (!scope) return;

    const lockedContainers: Array<{
      element: HTMLElement;
      overflowY: string;
    }> = [];
    let element = portalAnchorRef.current?.parentElement ?? null;

    // The scope itself is locked too, not just what sits inside it: on a page the scope *is* the
    // scrolling element, and leaving it loose lets the bulletin scroll under the open viewer.
    while (element) {
      const overflowY = window.getComputedStyle(element).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        lockedContainers.push({ element, overflowY: element.style.overflowY });
        element.style.overflowY = "hidden";
      }
      if (element === scope) break;
      element = element.parentElement;
    }

    return () => {
      lockedContainers.forEach(({ element, overflowY }) => {
        element.style.overflowY = overflowY;
      });
    };
  }, [isSlideShowActive]);

  const closeMediaDisplay = () => {
    setSelectedIndex(-1);
    setIsSlideShowActive(false);
  };

  const getModalContainer = () =>
    findScope(portalAnchorRef.current) ?? document.body;

  const mediaAsset =
    mediaType === "image" ? (
      <TransformWrapper>
        {({ zoomIn, zoomOut, resetTransform }) => (
          <div className="w-100 h-100 d-flex flex-column">
            <div className="w-100 d-flex pt-5 justify-content-center">
              <Tooltip title="Zoom In" placement="top">
                <IconButton
                  aria-label="Zoom in"
                  color="primary"
                  size="medium"
                  onClick={() => zoomIn()}
                >
                  <AddIcon style={{ fontSize: 36 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom Out" placement="top">
                <IconButton
                  aria-label="Zoom out"
                  color="primary"
                  size="medium"
                  onClick={() => zoomOut()}
                >
                  <RemoveIcon style={{ fontSize: 36 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reset Zoom" placement="top">
                <IconButton
                  aria-label="Reset zoom"
                  color="primary"
                  size="medium"
                  onClick={() => resetTransform()}
                >
                  <RestartAltIcon style={{ fontSize: 36 }} />
                </IconButton>
              </Tooltip>
            </div>
            <TransformComponent
              wrapperClass="w-100 h-100"
              contentClass="w-100 h-90"
            >
              <img
                src={mediaSrc}
                alt="media"
                className={styles.alert_full_image}
                onClick={() => {
                  if (selectedIndex < slideshowLength - 1)
                    setSelectedIndex(selectedIndex + 1);
                  else return;
                }}
              />
            </TransformComponent>
          </div>
        )}
      </TransformWrapper>
    ) : (
      <LazyLoadVideo
        src={mediaSrc}
        className={styles.alert_full_image}
        onClick={() => {
          if (selectedIndex < slideshowLength - 1)
            setSelectedIndex(selectedIndex + 1);
          else return;
        }}
      />
    );

  return (
    <>
      <span ref={portalAnchorRef} hidden />
      <Modal
        aria-label="Expanded media viewer"
        container={getModalContainer}
        open={isSlideShowActive}
        onClose={closeMediaDisplay}
        sx={
          isScoped
            ? {
                position: "absolute",
                "& .MuiBackdrop-root": { position: "absolute" },
              }
            : undefined
        }
      >
        <div className={styles.images_slide_container}>
          <div className={styles.image_slide}>{mediaAsset}</div>
          <div className={styles.close_arrow_icon}>
            <IconButton
              aria-label="Close expanded media"
              color="primary"
              size="medium"
              onClick={closeMediaDisplay}
            >
              <CancelTwoTone style={{ fontSize: 36 }} />
            </IconButton>
          </div>
          {/* ALLOW NAVIGATION IN CAROUSEL if there are multiple pictures */}
          {slideshowLength > 1 && (
            <>
              <div
                className={styles.back_arrow_icon}
                onClick={() => {
                  if (selectedIndex > 0) setSelectedIndex(selectedIndex - 1);
                }}
              >
                <IconButton
                  aria-label="Previous media"
                  color="default"
                  size="medium"
                  onClick={() => {
                    if (selectedIndex > 0) setSelectedIndex(selectedIndex - 1);
                    else setSelectedIndex(slideshowLength - 1);
                  }}
                >
                  <ArrowLeft style={{ fontSize: 36 }} />
                </IconButton>
              </div>
              <div
                className={styles.right_arrow_icon}
                onClick={() => {
                  if (selectedIndex < slideshowLength - 1)
                    setSelectedIndex(selectedIndex + 1);
                }}
              >
                <IconButton
                  aria-label="Next media"
                  color="default"
                  size="medium"
                  onClick={() => {
                    if (selectedIndex < slideshowLength - 1)
                      setSelectedIndex(selectedIndex + 1);
                    else setSelectedIndex(0);
                  }}
                >
                  <ArrowRight style={{ fontSize: 36 }} />
                </IconButton>
              </div>
            </>
          )}
          <Typography className={styles.bottom_counter_text} variant="h6">
            {selectedIndex + 1}/{slideshowLength}
          </Typography>
        </div>
      </Modal>
    </>
  );
};

export default CompareMediaDisplay;
